/**
 * Keypair generation modal component.
 * Allows readers to generate new X25519 keypairs in-browser,
 * with automatic download of both public and private key files.
 */

import { generateKeypair } from '../../api/crypto.js';
import { downloadKeypair } from '../../utils/download.js';
import { showToast } from './toast.js';

// Module-level state
let generatedKeys = null; // { publicKey, privateKey }
let status = 'idle'; // 'idle' | 'generating' | 'complete'

/**
 * Render the keypair generation modal.
 * @param {HTMLElement} container - Container element to render into
 * @param {Object} state - Modal state
 * @param {boolean} [state.visible=false] - Whether modal is visible
 * @param {string} [state.status='idle'] - Current status
 * @param {Object|null} [state.generatedKeys=null] - Generated keypair
 * @param {string|null} [state.error=null] - Error message
 */
export function renderKeygenModal(container, state = {}) {
  const {
    visible = false,
    status: stateStatus = 'idle',
    generatedKeys: stateKeys = null,
    error = null
  } = state;

  if (!visible) {
    container.innerHTML = '';
    return;
  }

  // Build content based on status
  let contentHtml = '';

  if (stateStatus === 'idle') {
    contentHtml = `
      <p class="text-sm text-gray-600 mb-4">
        Generate a new X25519 keypair for receiving encrypted payloads.
      </p>
      <p class="text-sm text-gray-600 mb-6">
        Share your <strong>PUBLIC</strong> key with senders. Keep your <strong>PRIVATE</strong> key secret.
      </p>
      <button
        id="keygen-generate-btn"
        class="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Generate Keypair
      </button>
    `;
  } else if (stateStatus === 'generating') {
    contentHtml = `
      <p class="text-sm text-gray-600 mb-4">
        Generate a new X25519 keypair for receiving encrypted payloads.
      </p>
      <p class="text-sm text-gray-600 mb-6">
        Share your <strong>PUBLIC</strong> key with senders. Keep your <strong>PRIVATE</strong> key secret.
      </p>
      <div class="flex items-center justify-center mb-4">
        <svg class="animate-spin h-5 w-5 text-blue-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-gray-600">Generating...</span>
      </div>
      <button
        disabled
        class="w-full px-4 py-2 bg-gray-400 text-white rounded-md cursor-not-allowed"
      >
        Generating...
      </button>
    `;
  } else if (stateStatus === 'complete' && stateKeys) {
    contentHtml = `
      <div class="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
        <p class="text-sm text-green-800 font-medium">Keypair generated successfully!</p>
      </div>
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Your Public Key (share this with senders)
        </label>
        <div class="flex gap-2">
          <textarea
            id="keygen-public-key"
            readonly
            class="flex-1 px-3 py-2 text-xs font-mono bg-gray-50 border border-gray-300 rounded-md resize-none"
            rows="2"
          >${stateKeys.publicKey}</textarea>
          <button
            id="keygen-copy-btn"
            class="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
            title="Copy to clipboard"
          >
            <svg class="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
            </svg>
          </button>
        </div>
      </div>
      <p class="text-sm text-gray-600 mb-4">
        Your key files have been downloaded.
      </p>
      <button
        id="keygen-download-again-btn"
        class="w-full px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-200 transition-colors mb-4"
      >
        Download Keys Again
      </button>
      <div class="bg-yellow-50 border border-yellow-200 rounded-md p-3">
        <p class="text-sm text-yellow-800">
          <strong>Warning:</strong> Store your private key securely. It cannot be recovered.
        </p>
      </div>
    `;
  }

  // Add error message if present
  const errorHtml = error ? `
    <div class="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
      <p class="text-sm text-red-800">${error}</p>
    </div>
  ` : '';

  container.innerHTML = `
    <div
      id="keygen-modal-backdrop"
      class="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">Generate New Keypair</h2>
          <button
            id="keygen-close-btn"
            class="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="px-6 py-4">
          ${errorHtml}
          ${contentHtml}
        </div>
        <div class="flex justify-end px-6 py-4 border-t border-gray-200">
          <button
            id="keygen-cancel-btn"
            class="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Initialize the keypair generation modal.
 * Sets up event handlers for generation and downloads.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} [callbacks.onClose] - Called when modal is closed
 * @param {Function} [callbacks.onGenerate] - Called with publicKey after generation
 * @returns {Function} Cleanup function
 */
export function initKeygenModal(container, callbacks = {}) {
  const { onClose, onGenerate } = callbacks;

  // Close button handler
  const closeBtn = container.querySelector('#keygen-close-btn');
  const cancelBtn = container.querySelector('#keygen-cancel-btn');
  const backdrop = container.querySelector('#keygen-modal-backdrop');

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  function handleBackdropClick(e) {
    // Only close if clicking directly on backdrop, not modal content
    if (e.target === backdrop) {
      handleClose();
    }
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', handleClose);
  }

  if (cancelBtn) {
    cancelBtn.addEventListener('click', handleClose);
  }

  if (backdrop) {
    backdrop.addEventListener('click', handleBackdropClick);
  }

  // Generate button handler
  const generateBtn = container.querySelector('#keygen-generate-btn');

  async function handleGenerate() {
    try {
      // Update status to generating
      status = 'generating';
      renderKeygenModal(container, { visible: true, status, generatedKeys, error: null });
      initKeygenModal(container, callbacks);

      // Generate keypair
      const keys = await generateKeypair();
      generatedKeys = keys;
      status = 'complete';

      // Auto-download both keys
      downloadKeypair(keys.publicKey, keys.privateKey, 'reader');

      // Re-render with complete state
      renderKeygenModal(container, { visible: true, status, generatedKeys, error: null });
      initKeygenModal(container, callbacks);

      // Show success toast
      showToast('Keypair generated and downloaded!', 'success');

      // Call onGenerate callback with public key
      if (onGenerate) {
        onGenerate(keys.publicKey);
      }
    } catch (err) {
      // Handle error
      status = 'idle';
      const errorMessage = err.message || 'Failed to generate keypair';
      renderKeygenModal(container, { visible: true, status, generatedKeys: null, error: errorMessage });
      initKeygenModal(container, callbacks);
      showToast('Failed to generate keypair', 'error');
    }
  }

  if (generateBtn) {
    generateBtn.addEventListener('click', handleGenerate);
  }

  // Copy button handler
  const copyBtn = container.querySelector('#keygen-copy-btn');

  async function handleCopy() {
    const publicKeyEl = container.querySelector('#keygen-public-key');
    if (publicKeyEl && publicKeyEl.value) {
      try {
        await navigator.clipboard.writeText(publicKeyEl.value);
        showToast('Public key copied to clipboard!', 'success');
      } catch (err) {
        // Fallback: select text
        publicKeyEl.select();
        showToast('Please copy the selected text manually', 'info');
      }
    }
  }

  if (copyBtn) {
    copyBtn.addEventListener('click', handleCopy);
  }

  // Download again button handler
  const downloadAgainBtn = container.querySelector('#keygen-download-again-btn');

  function handleDownloadAgain() {
    if (generatedKeys) {
      downloadKeypair(generatedKeys.publicKey, generatedKeys.privateKey, 'reader');
      showToast('Key files downloaded!', 'success');
    }
  }

  if (downloadAgainBtn) {
    downloadAgainBtn.addEventListener('click', handleDownloadAgain);
  }

  // Return cleanup function
  return () => {
    if (closeBtn) {
      closeBtn.removeEventListener('click', handleClose);
    }
    if (cancelBtn) {
      cancelBtn.removeEventListener('click', handleClose);
    }
    if (backdrop) {
      backdrop.removeEventListener('click', handleBackdropClick);
    }
    if (generateBtn) {
      generateBtn.removeEventListener('click', handleGenerate);
    }
    if (copyBtn) {
      copyBtn.removeEventListener('click', handleCopy);
    }
    if (downloadAgainBtn) {
      downloadAgainBtn.removeEventListener('click', handleDownloadAgain);
    }
  };
}

/**
 * Show the keypair generation modal.
 * Resets state and renders the modal in visible state.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} [callbacks.onClose] - Called when modal is closed
 * @param {Function} [callbacks.onGenerate] - Called with publicKey after generation
 * @returns {Function} Cleanup function
 */
export function showKeygenModal(container, callbacks = {}) {
  // Reset state
  status = 'idle';
  generatedKeys = null;

  // Render and initialize
  renderKeygenModal(container, { visible: true, status, generatedKeys, error: null });
  return initKeygenModal(container, callbacks);
}

/**
 * Hide the keypair generation modal.
 * Clears the container content.
 * @param {HTMLElement} container - Container element
 */
export function hideKeygenModal(container) {
  container.innerHTML = '';
}
